import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  computeCourierMeasures,
  canMarkCourierShipped,
  shippedRequiredMissing,
  isCourierFormLocked,
  isSpainCourierEnvio,
  resolveCourierShipFromKey,
} from "../../utils/courierControlPure";
import { lockedDialogSlotProps } from "../../utils/lockedDialogUi";
import {
  COURIER_COMPANIES,
  COURIER_MOTIVO_OPTIONS,
  COURIER_PACKING_OPTIONS,
  STAGE_CHINA_ORIGIN,
  buildOriginOptionsForUser,
  courierMotivoI18nKey,
  courierPackingI18nKey,
  destinationTiposForCourierUser,
  destinoLabelFromEnvioDestino,
  filterRequestedByUsers,
  inferCountryFromOrigin,
  inferEnvioDestinoFromCourier,
  isChinaCourierUser,
  isStageChinaUser,
  isCourierLeftWithClientMotivo,
  isCourierLeftWithClientEnvio,
  filterOptionsStartsWith,
  userDisplayName,
} from "../../utils/courierControlSelects";
import {
  searchMuestrasCourier,
  getCourierMuestraLineaFields,
  downloadCourierInformePdf,
  downloadCourierFacturaPdf,
} from "../../api/CourierControl/courierControlApi";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  isCourierSampleMotivo,
  shouldShowSampleDetails,
  shouldShowPieceMetaFields,
  isPieceMetaReadOnly,
  sampleDetailsChanged,
  normalizeCourierMotivoValue,
} from "../../utils/courierSamplePiece";
import { classifyCourierPiezasKind } from "../../utils/courierControlPure";
import { getProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { getMarcas } from "../../api/Marcas/marcaApi";
import { getAllUsers } from "../../api/User/userApi";
import EnvioDestinoFields, {
  emptyEnvioDestino,
} from "../Muestras/EnvioDestinoFields";
import { pageFieldSx } from "../../styles/pageModernStyles";

export const emptyLine = (partial = {}) => ({
  _clientKey:
    partial._clientKey ||
    `ln-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
  Temporada: "",
  Marca: "",
  Estilo: "",
  TipoPrenda: "",
  PesoKg: "",
  Tejido: "",
  Color: "",
  Talla: "",
  TipoMuestra: "",
  CodigoMuestra: "",
  IdMuestra: null,
  IdMovimientoMuestra: null,
  Motivo: "sample",
  SolicitadoPor: "",
  ContactoDestino: "",
  ...partial,
});

export const emptyEnvio = (partial = {}) => ({
  Direccion: "SALIDA",
  Ambito: "NACIONAL",
  Fecha: new Date().toISOString().slice(0, 10),
  FechaEnvio: "",
  FechaEntregaDestino: "",
  SolicitadoPor: "",
  Origen: "",
  Destino: "",
  DireccionEnvio: "",
  Contacto: "",
  TipoEmbalaje: "",
  LargoCm: "",
  AnchoCm: "",
  AltoCm: "",
  PesoBrutoKg: "",
  CompaniaCourier: "",
  NumeroAwb: "",
  Coste: "",
  CosteReal: "",
  Pais: "",
  Estado: "PEDIDO",
  Notas: "",
  lineas: [emptyLine()],
  ...partial,
});

const fmt = (value, digits = 3) => {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

/** Pedido azul · Preparando aduanas índigo · Enviado naranja · Entregado verde · Cancelado rojo */
const statusAccent = (theme, estado) => {
  const e = String(estado || "PEDIDO").toUpperCase();
  if (e === "PREPARANDO_ADUANAS")
    return theme.palette.info.dark || theme.palette.secondary.main;
  if (e === "EN_CURSO") return theme.palette.warning.main;
  if (e === "ENTREGADO") return theme.palette.success.main;
  if (e === "CANCELADO") return theme.palette.error.main;
  return theme.palette.info.main; // PEDIDO
};

const statusColor = (estado) => {
  const e = String(estado || "").toUpperCase();
  if (e === "ENTREGADO") return "success";
  if (e === "PREPARANDO_ADUANAS") return "secondary";
  if (e === "EN_CURSO") return "warning";
  if (e === "CANCELADO") return "error";
  return "info"; // PEDIDO
};

const FormPanel = ({ icon, title, subtitle, accent, children, className }) => {
  const theme = useTheme();
  const color = accent || theme.palette.primary.main;
  return (
    <Box
      className={className}
      sx={{
        mb: 2,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: alpha(color, 0.18),
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 6px 20px rgba(0,0,0,0.28)"
            : `0 8px 24px ${alpha(color, 0.07)}`,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={{
          px: 2,
          py: 1.4,
          borderBottom: "1px solid",
          borderColor: alpha(color, 0.12),
          background: `linear-gradient(120deg, ${alpha(color, 0.1)} 0%, ${alpha(
            theme.palette.background.paper,
            0.2,
          )} 70%)`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.75,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(color, 0.14),
            color,
            flexShrink: 0,
            mt: 0.1,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, pt: 0.15 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.78rem",
              letterSpacing: 0.7,
              textTransform: "uppercase",
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ px: 2, py: 1.75 }}>{children}</Box>
    </Box>
  );
};

function SampleCodeField({ value, onChange, fieldSx, disabled = false }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const q = String(value.CodigoMuestra || "").trim();
    if (q.length < 2 || disabled) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const rows = await searchMuestrasCourier(q);
        if (!cancelled) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setOptions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value.CodigoMuestra, disabled]);

  return (
    <Autocomplete
      freeSolo
      autoSelect={false}
      autoHighlight={false}
      clearOnBlur={false}
      selectOnFocus={false}
      handleHomeEndKeys
      size="small"
      disabled={disabled}
      options={options}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        const modelo = String(option.NombreCliente || "").trim();
        const samp = String(option.MUESTRA || "").trim();
        if (modelo && samp && modelo.toLowerCase() !== samp.toLowerCase()) {
          return `${modelo} · ${samp}`;
        }
        return modelo || samp || "";
      }}
      isOptionEqualToValue={(opt, val) => {
        if (typeof opt === "string" || typeof val === "string") {
          return String(opt) === String(val);
        }
        return Number(opt?.ID_MUESTRA) === Number(val?.ID_MUESTRA);
      }}
      value={value.CodigoMuestra || ""}
      inputValue={value.CodigoMuestra || ""}
      onInputChange={(_, next, reason) => {
        // Solo tecleo/borrado: no forzar selección Matrix
        if (disabled || reason === "reset") return;
        if (reason !== "input" && reason !== "clear") return;
        onChange({
          ...value,
          CodigoMuestra: next,
          IdMuestra: null,
          // Liberar modelo TON al escribir texto libre
          Estilo: "",
        });
      }}
      onChange={(_, next) => {
        if (disabled) return;
        if (next && typeof next === "object") {
          const samp = String(next.MUESTRA || "").trim();
          const modelo = String(next.NombreCliente || "").trim();
          onChange({
            ...value,
            CodigoMuestra: samp,
            Estilo:
              modelo && modelo.toLowerCase() !== samp.toLowerCase()
                ? modelo
                : "",
            IdMuestra: next.ID_MUESTRA,
          });
          return;
        }
        // Texto libre (no existe en Matrix): conservar y dejar meta editable
        const free = String(next || "").trim();
        onChange({
          ...value,
          CodigoMuestra: free,
          IdMuestra: null,
          Estilo: "",
        });
      }}
      renderOption={(props, option) => {
        const modelo = String(option.NombreCliente || "").trim();
        const samp = String(option.MUESTRA || "").trim();
        const marca = String(option.Marca || "").trim();
        return (
          <li {...props} key={option.ID_MUESTRA}>
            <Box sx={{ py: 0.25, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {modelo || samp || `#${option.ID_MUESTRA}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[samp && modelo ? samp : null, marca]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("courierControl.sampleCode")}
          sx={fieldSx}
          helperText={
            value.IdMovimientoMuestra
              ? t("courierControl.linkedFromShipment")
              : value.IdMuestra
                ? value.Estilo
                  ? t("courierControl.linkedSampleModel", {
                      model: value.Estilo,
                      samp: value.CodigoMuestra || "",
                    })
                  : t("courierControl.linkedSampleOnly")
                : t("courierControl.sampleHintFree", {
                    defaultValue: t("courierControl.sampleHint"),
                  })
          }
        />
      )}
    />
  );
}

function PieceCard({
  linea,
  index,
  onChange,
  onRemove,
  canRemove,
  requestDisabled,
  pieceFieldsDisabled,
  preferLeftWithClient = false,
  expanded = false,
  onToggleExpand,
  previousLine = null,
  fieldSx,
  t,
}) {
  const theme = useTheme();
  const fieldsLocked = Boolean(pieceFieldsDisabled || requestDisabled);
  const isSampleLine =
    isCourierSampleMotivo(linea.Motivo) ||
    Boolean(linea.IdMuestra || linea.IdMovimientoMuestra);
  const hasSamp = Boolean(
    String(linea.CodigoMuestra || "").trim() ||
    linea.IdMuestra ||
    linea.IdMovimientoMuestra,
  );
  const hasContent = Boolean(String(linea.Estilo || "").trim());
  const showSamp = isSampleLine || hasSamp || !hasContent;
  // Estilo en muestras = código de modelo (no contenido libre)
  const showContent = !isSampleLine && (hasContent || !hasSamp);
  const showSampleDetails = shouldShowSampleDetails(linea);
  const showPieceMeta = shouldShowPieceMetaFields(linea);
  const metaReadOnly = isPieceMetaReadOnly(linea) || fieldsLocked;
  const modelLabel = String(linea.Estilo || "").trim();
  const sampLabel = String(linea.CodigoMuestra || "").trim();
  const title =
    (isSampleLine && modelLabel) ||
    sampLabel ||
    modelLabel ||
    `${t("courierControl.piece")} ${index + 1}`;
  const titleSub =
    isSampleLine && modelLabel && sampLabel && modelLabel !== sampLabel
      ? sampLabel
      : "";
  const personaLineHint = [
    String(linea.SolicitadoPor || "").trim() &&
      `${t("courierControl.lineRequestedByShort")}: ${String(linea.SolicitadoPor).trim()}`,
    String(linea.ContactoDestino || "").trim() &&
      `${t("courierControl.lineContactShort")}: ${String(linea.ContactoDestino).trim()}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const openDetails = Boolean(expanded);
  const toggleDetails = () => {
    if (typeof onToggleExpand === "function") onToggleExpand();
  };
  const motivoValue = normalizeCourierMotivoValue(linea.Motivo);
  const motivoKnown = COURIER_MOTIVO_OPTIONS.includes(motivoValue);
  const motivoRaw = String(linea.Motivo || "").trim();
  const motivoChipLabel = t(
    courierMotivoI18nKey(motivoValue) ||
      `courierControl.reason${String(motivoValue || "sample")
        .charAt(0)
        .toUpperCase()}${String(motivoValue || "sample").slice(1)}`,
  );
  const isLeaveMotivo = motivoValue === "leftWithClient";

  // Normaliza Motivo legado TON → sample / leftWithClient
  useEffect(() => {
    if (!motivoRaw || motivoRaw === motivoValue) return;
    if (motivoValue === "leftWithClient") {
      onChange({ ...linea, Motivo: "leftWithClient" });
      return;
    }
    if (isCourierSampleMotivo(motivoRaw) && motivoValue === "sample") {
      onChange({ ...linea, Motivo: "sample" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motivoRaw, motivoValue]);

  useEffect(() => {
    if (!showSampleDetails) return undefined;
    let cancelled = false;

    const applyFields = (fields) => {
      if (cancelled || !fields) return;
      const preferLeave =
        preferLeftWithClient ||
        isCourierLeftWithClientMotivo(linea.Motivo) ||
        normalizeCourierMotivoValue(linea.Motivo) === "leftWithClient";
      const currentMotivo = normalizeCourierMotivoValue(linea.Motivo);
      const keepMotivo = preferLeave
        ? "leftWithClient"
        : currentMotivo === "leftWithClient"
          ? "leftWithClient"
          : COURIER_MOTIVO_OPTIONS.includes(currentMotivo) &&
              currentMotivo !== "sample"
            ? currentMotivo
            : "sample";
      const { Motivo: _ignoreMotivo, ...fieldsWithoutMotivo } = fields;
      const merged = {
        ...linea,
        ...fieldsWithoutMotivo,
        Motivo: keepMotivo,
        IdMovimientoMuestra: linea.IdMovimientoMuestra,
      };
      if (sampleDetailsChanged(linea, merged)) onChange(merged);
    };

    const run = async () => {
      let id = Number(linea.IdMuestra);
      if (!Number.isFinite(id) || id <= 0) {
        const code = String(linea.CodigoMuestra || "").trim();
        if (!code) return;
        const rows = await searchMuestrasCourier(code);
        // Solo coincidencia exacta: nunca coger el primer resultado al escribir
        const hit = (Array.isArray(rows) ? rows : []).find(
          (r) =>
            String(r.MUESTRA || "")
              .trim()
              .toLowerCase() === code.toLowerCase() ||
            String(r.NombreCliente || "")
              .trim()
              .toLowerCase() === code.toLowerCase(),
        );
        id = Number(hit?.ID_MUESTRA);
      }
      if (!Number.isFinite(id) || id <= 0) return;
      const fields = await getCourierMuestraLineaFields(id);
      applyFields(fields);
    };

    run().catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSampleDetails, linea.IdMuestra, linea.CodigoMuestra]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isLeaveMotivo
          ? alpha("#6a1b9a", 0.35)
          : alpha(theme.palette.divider, 0.75),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.96),
        overflow: "hidden",
        mb: 1,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          px: 1.25,
          py: 0.85,
          bgcolor: isLeaveMotivo
            ? alpha("#6a1b9a", 0.06)
            : alpha(theme.palette.primary.main, 0.03),
          borderBottom: openDetails ? "1px solid" : "none",
          borderColor: alpha(theme.palette.divider, 0.5),
          cursor: "pointer",
        }}
        onClick={toggleDetails}
      >
        <Typography
          sx={{ fontWeight: 800, fontSize: "0.88rem", flex: 1, minWidth: 0 }}
          noWrap
        >
          {title}
          {titleSub ? (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontWeight: 600 }}
            >
              {titleSub}
            </Typography>
          ) : null}
          {personaLineHint ? (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 600, mt: 0.15 }}
              noWrap
            >
              {personaLineHint}
            </Typography>
          ) : null}
        </Typography>
        <Chip
          size="small"
          label={motivoChipLabel}
          onClick={(e) => {
            e.stopPropagation();
            if (!openDetails) toggleDetails();
          }}
          sx={{
            height: 24,
            fontWeight: 800,
            fontSize: "0.68rem",
            ...(isLeaveMotivo
              ? {
                  bgcolor: alpha("#6a1b9a", 0.12),
                  color: "#6a1b9a",
                  border: `1px solid ${alpha("#6a1b9a", 0.4)}`,
                }
              : {}),
          }}
          variant={isLeaveMotivo ? "filled" : "outlined"}
        />
        {linea.IdMovimientoMuestra ? (
          <Chip
            size="small"
            label={t("courierControl.fromMatrixShipment")}
            sx={{
              height: 22,
              fontWeight: 700,
              display: { xs: "none", md: "flex" },
            }}
            color="primary"
            variant="outlined"
          />
        ) : null}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            toggleDetails();
          }}
          aria-label={t("courierControl.pieceDetails")}
        >
          {openDetails ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
        {canRemove ? (
          <IconButton
            size="small"
            aria-label={t("courierControl.deleteLine")}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={fieldsLocked}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Collapse in={openDetails} unmountOnExit>
        <Box sx={{ p: 1.5 }}>
          <Grid container spacing={1.25}>
            {showSamp ? (
              <Grid item xs={12} sm={showContent ? 4 : 5}>
                <SampleCodeField
                  value={linea}
                  fieldSx={fieldSx}
                  disabled={fieldsLocked || Boolean(linea.IdMovimientoMuestra)}
                  onChange={(next) => {
                    const code = String(next.CodigoMuestra || "").trim();
                    onChange({
                      ...next,
                      Estilo:
                        code || next.IdMuestra
                          ? next.Estilo || ""
                          : next.Estilo || "",
                      Motivo:
                        code || next.IdMuestra
                          ? normalizeCourierMotivoValue(next.Motivo) ===
                            "leftWithClient"
                            ? "leftWithClient"
                            : isCourierSampleMotivo(next.Motivo) || !next.Motivo
                              ? "sample"
                              : next.Motivo
                          : next.Motivo,
                    });
                  }}
                />
              </Grid>
            ) : null}
            {showContent ? (
              <Grid item xs={12} sm={showSamp ? 4 : 5}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.style")}
                  value={linea.Estilo || ""}
                  disabled={fieldsLocked}
                  sx={fieldSx}
                  onChange={(e) => {
                    const estilo = e.target.value;
                    onChange({
                      ...linea,
                      Estilo: estilo,
                      ...(String(estilo || "").trim()
                        ? {
                            CodigoMuestra: "",
                            IdMuestra: null,
                          }
                        : null),
                    });
                  }}
                />
              </Grid>
            ) : null}
            <Grid item xs={12} sm={showSamp && showContent ? 3 : 5}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("courierControl.reason")}
                value={motivoKnown ? motivoValue : motivoRaw || ""}
                disabled={fieldsLocked}
                sx={fieldSx}
                onChange={(e) => {
                  const nextMotivo = e.target.value;
                  const next = { ...linea, Motivo: nextMotivo };
                  // Al salir de muestra, soltar vínculo TON pero conservar meta editable
                  if (
                    ["swatch", "color", "card", "other"].includes(nextMotivo)
                  ) {
                    onChange({
                      ...next,
                      CodigoMuestra: "",
                      IdMuestra: null,
                      IdMovimientoMuestra: null,
                    });
                  } else {
                    onChange(next);
                  }
                }}
              >
                {motivoRaw &&
                !motivoKnown &&
                !isCourierSampleMotivo(motivoRaw) ? (
                  <MenuItem value={motivoRaw}>{motivoRaw}</MenuItem>
                ) : null}
                {COURIER_MOTIVO_OPTIONS.map((key) => (
                  <MenuItem key={key} value={key}>
                    {t(
                      courierMotivoI18nKey(key) ||
                        `courierControl.reason${key.charAt(0).toUpperCase()}${key.slice(1)}`,
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={showSamp && showContent ? 3 : 4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: "0.001", min: "0" }}
                label={t("courierControl.lineWeight")}
                value={linea.PesoKg ?? ""}
                disabled={fieldsLocked}
                sx={fieldSx}
                helperText={t("courierControl.lineWeightHint")}
                onChange={(e) => onChange({ ...linea, PesoKg: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.lineRequestedBy")}
                value={linea.SolicitadoPor || ""}
                disabled={fieldsLocked}
                sx={fieldSx}
                onChange={(e) =>
                  onChange({ ...linea, SolicitadoPor: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label={t("courierControl.lineContact")}
                value={linea.ContactoDestino || ""}
                disabled={fieldsLocked}
                sx={fieldSx}
                onChange={(e) =>
                  onChange({ ...linea, ContactoDestino: e.target.value })
                }
              />
            </Grid>
          </Grid>

          {showPieceMeta ? (
            <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
              {!metaReadOnly &&
              index > 0 &&
              previousLine &&
              (String(previousLine.Temporada || "").trim() ||
                String(previousLine.Marca || "").trim()) ? (
                <Grid item xs={12}>
                  <Button
                    size="small"
                    variant="text"
                    disabled={fieldsLocked}
                    onClick={() =>
                      onChange({
                        ...linea,
                        Temporada:
                          previousLine.Temporada || linea.Temporada || "",
                        Marca: previousLine.Marca || linea.Marca || "",
                      })
                    }
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      px: 0.5,
                      minHeight: 28,
                    }}
                  >
                    {t("courierControl.copySeasonBrand")}
                  </Button>
                </Grid>
              ) : null}
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.season")}
                  value={linea.Temporada || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, Temporada: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.brand")}
                  value={linea.Marca || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, Marca: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.garmentType")}
                  value={linea.TipoPrenda || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, TipoPrenda: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.sampleType")}
                  value={linea.TipoMuestra || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, TipoMuestra: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.fabric")}
                  value={linea.Tejido || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, Tejido: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.color")}
                  value={linea.Color || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, Color: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.size")}
                  value={linea.Talla || ""}
                  disabled={metaReadOnly}
                  sx={fieldSx}
                  onChange={(e) =>
                    onChange({ ...linea, Talla: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function CourierControlDialog({
  open,
  envio,
  saving,
  onClose,
  onChange,
  onSave,
  onDelete,
  canSolicitar = false,
  canTerminar = false,
  currentUser = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fieldSx = pageFieldSx(theme);
  // Pedido: editable. En preparación: solo Terminar. Enviado/Entregado/Cancelado: bloqueado.
  const estadoUpper = String(envio.Estado || "PEDIDO").toUpperCase();
  const formLocked = isCourierFormLocked(estadoUpper, { canTerminar });
  const piecesLocked = formLocked;
  const shippedLocked = formLocked;
  const hasEditPerm = canSolicitar || canTerminar;
  const requestDisabled = !hasEditPerm || formLocked;
  const finishDisabled = !hasEditPerm || formLocked;
  // Coste real recibido: editable con Terminar aunque el envío esté Enviado
  const costeRealDisabled = !hasEditPerm || (formLocked && !canTerminar);
  const statusDisabled = canTerminar ? false : !canSolicitar || formLocked;
  const addPieceDisabled = !hasEditPerm || piecesLocked;
  const canSave = canTerminar || (canSolicitar && !formLocked);
  const canDelete = Boolean(envio.IdCourierControl) && canTerminar;
  const [docsBusy, setDocsBusy] = useState(null);
  const [facturaMenuAnchor, setFacturaMenuAnchor] = useState(null);
  const canDownloadDocs =
    Boolean(envio.IdCourierControl) &&
    Array.isArray(envio.lineas) &&
    envio.lineas.length > 0;
  const suggestedShipFrom = useMemo(
    () =>
      resolveCourierShipFromKey({
        origen: envio.Origen,
        lineas: envio.lineas,
      }),
    [envio.Origen, envio.lineas],
  );

  const handleDownloadDoc = async (kind, { shipFrom } = {}) => {
    const id = Number(envio.IdCourierControl);
    if (!Number.isFinite(id) || id <= 0) return;
    setDocsBusy(kind);
    setFacturaMenuAnchor(null);
    try {
      if (kind === "informe") await downloadCourierInformePdf(id);
      else await downloadCourierFacturaPdf(id, { shipFrom });
    } catch (err) {
      console.error("courier docs download:", err);
      window.alert(
        kind === "informe"
          ? t("courierControl.docsInformeError")
          : t("courierControl.docsFacturaError"),
      );
    } finally {
      setDocsBusy(null);
    }
  };
  const showPreparandoAduanas =
    canTerminar &&
    (isSpainCourierEnvio(envio) || estadoUpper === "PREPARANDO_ADUANAS");
  const isChinaUser = isChinaCourierUser(currentUser || {});
  const isStageUser = isStageChinaUser(currentUser || {});
  const originLocked = isStageUser;
  const [pendingRemove, setPendingRemove] = useState(null); // { index, label, linked }
  const [confirmShipOpen, setConfirmShipOpen] = useState(false);
  const [shipAck, setShipAck] = useState(false);
  const [shipMissing, setShipMissing] = useState([]);
  const [confirmDeliveredOpen, setConfirmDeliveredOpen] = useState(false);
  const [deliverAck, setDeliverAck] = useState(false);
  const [deliverDateDraft, setDeliverDateDraft] = useState("");
  const [proveedores, setProveedores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [users, setUsers] = useState([]);
  const [openPieceKey, setOpenPieceKey] = useState(null);
  const [repeatSeasonBrand, setRepeatSeasonBrand] = useState(true);
  const [destinoForm, setDestinoForm] = useState(() => emptyEnvioDestino());

  const deliveredSampleLines = useMemo(() => {
    const lineas = Array.isArray(envio?.lineas) ? envio.lineas : [];
    return lineas.filter(
      (l) =>
        Number(l.IdMuestra) > 0 ||
        String(l.CodigoMuestra || "").trim() ||
        String(l.Estilo || "").trim(),
    );
  }, [envio?.lineas]);

  const isLeaveClientEnvio = useMemo(
    () => isCourierLeftWithClientEnvio(envio),
    [envio],
  );
  const piezasKind = useMemo(
    () => classifyCourierPiezasKind(envio?.lineas || []),
    [envio?.lineas],
  );
  const piezasCount = Math.max(
    Number(envio?.lineas?.length) || 0,
    piezasKind.count,
  );
  const packingIsHand =
    String(envio?.TipoEmbalaje || "")
      .trim()
      .toLowerCase() === "en mano";
  const [boxDetailsOpen, setBoxDetailsOpen] = useState(!packingIsHand);

  useEffect(() => {
    if (packingIsHand || isLeaveClientEnvio) setBoxDetailsOpen(false);
  }, [packingIsHand, isLeaveClientEnvio, envio?.IdCourierControl]);

  const measures = useMemo(
    () =>
      computeCourierMeasures({
        largoCm: envio.LargoCm,
        anchoCm: envio.AnchoCm,
        altoCm: envio.AltoCm,
        pesoBrutoKg: envio.PesoBrutoKg,
        coste: envio.Coste,
        costeReal: envio.CosteReal,
      }),
    [envio],
  );

  const originOptions = useMemo(
    () =>
      buildOriginOptionsForUser(proveedores, currentUser || {}, {
        customers,
        marcas,
      }),
    [proveedores, currentUser, customers, marcas],
  );

  const pieceKeyOf = (linea, index) =>
    String(linea?._clientKey || linea?.IdCourierControlLinea || `idx-${index}`);

  const addPiece = () => {
    const key = `ln-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const lines = Array.isArray(envio.lineas) ? envio.lineas : [];
    const prev =
      [...lines]
        .reverse()
        .find(
          (l) =>
            String(l?.Temporada || "").trim() || String(l?.Marca || "").trim(),
        ) || lines[lines.length - 1];
    const inherited =
      repeatSeasonBrand && prev
        ? {
            Temporada: String(prev.Temporada || "").trim(),
            Marca: String(prev.Marca || "").trim(),
          }
        : {};
    onChange({
      ...envio,
      lineas: [...lines, emptyLine({ _clientKey: key, ...inherited })],
    });
    setOpenPieceKey(key);
  };

  useEffect(() => {
    if (!open) {
      setOpenPieceKey(null);
      setDestinoForm(emptyEnvioDestino());
      return;
    }
    const lines = Array.isArray(envio.lineas) ? envio.lineas : [];
    if (
      lines.length === 1 &&
      !lines[0]?.IdCourierControlLinea &&
      !String(lines[0]?.CodigoMuestra || "").trim() &&
      !String(lines[0]?.Estilo || "").trim()
    ) {
      setOpenPieceKey(pieceKeyOf(lines[0], 0));
    }
    // Solo al abrir el diálogo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, envio.IdCourierControl]);

  const allowedDestinoTipos = useMemo(
    () => destinationTiposForCourierUser(currentUser || {}),
    [currentUser],
  );

  const requestedByOptions = useMemo(
    () => filterRequestedByUsers(users, currentUser || {}, proveedores),
    [users, currentUser, proveedores],
  );

  const userNameOptions = useMemo(() => {
    const names = users.map(userDisplayName).filter(Boolean);
    const current = String(envio.SolicitadoPor || "").trim();
    if (current && !names.includes(current)) names.unshift(current);
    return names;
  }, [users, envio.SolicitadoPor]);

  const courierCompanyOptions = useMemo(() => {
    const current = String(envio.CompaniaCourier || "").trim();
    if (current && !COURIER_COMPANIES.includes(current)) {
      return [current, ...COURIER_COMPANIES];
    }
    return COURIER_COMPANIES;
  }, [envio.CompaniaCourier]);

  useEffect(() => {
    if (!open) {
      setConfirmShipOpen(false);
      setShipAck(false);
      setShipMissing([]);
      setConfirmDeliveredOpen(false);
      setDeliverAck(false);
      setPendingRemove(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.all([
      getProveedores().catch(() => []),
      getCustomers().catch(() => []),
      getMarcas().catch(() => []),
      getAllUsers().catch(() => []),
    ]).then(([prov, cust, marks, usr]) => {
      if (cancelled) return;
      setProveedores(Array.isArray(prov) ? prov : []);
      setCustomers(Array.isArray(cust) ? cust : []);
      setMarcas(Array.isArray(marks) ? marks : []);
      setUsers(Array.isArray(usr) ? usr : []);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDestinoForm((prev) => {
      const inferred = inferEnvioDestinoFromCourier(envio, {
        customers,
        proveedores,
      });
      const prevPersona = String(prev?.PERSONA_ENVIO || "").trim();
      const inferredPersona = String(inferred.PERSONA_ENVIO || "").trim();
      // Dirección: SIEMPRE la del envío abierto (nunca la del diálogo anterior)
      const savedDir = String(envio.DireccionEnvio || "").trim();
      if (prevPersona && !inferredPersona) {
        return {
          ...inferred,
          TIPO_DESTINO: inferred.TIPO_DESTINO || prev.TIPO_DESTINO,
          ID_CLIENTE: inferred.ID_CLIENTE ?? prev.ID_CLIENTE,
          CODPROV: inferred.CODPROV ?? prev.CODPROV,
          ID_CONTACTO: prev.ID_CONTACTO ?? inferred.ID_CONTACTO,
          PERSONA_ENVIO: prev.PERSONA_ENVIO,
          DIRECCION_ENVIO: savedDir,
        };
      }
      return {
        ...inferred,
        DIRECCION_ENVIO: savedDir,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    envio.IdCourierControl,
    envio.Contacto,
    envio.DireccionEnvio,
    customers,
    proveedores,
  ]);

  // Stage China: origen fijo + país; Solicitado por = usuario actual si vacío
  useEffect(() => {
    if (!open || !currentUser) return;
    const patch = {};
    if (isStageUser) {
      if (envio.Origen !== STAGE_CHINA_ORIGIN) {
        patch.Origen = STAGE_CHINA_ORIGIN;
      }
      if (!envio.Pais || /china/i.test(String(envio.Pais))) {
        patch.Pais = "China";
      }
    }
    if (isChinaUser && !String(envio.SolicitadoPor || "").trim()) {
      patch.SolicitadoPor = userDisplayName(currentUser);
    }
    if (Object.keys(patch).length) {
      onChange({ ...envio, ...patch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isStageUser, isChinaUser, currentUser?.id]);

  const setField = (field, value) => onChange({ ...envio, [field]: value });

  const requestMarkShipped = () => {
    const missing = shippedRequiredMissing(envio);
    if (missing.length) {
      setShipMissing(missing);
      setConfirmShipOpen(false);
      setShipAck(false);
      return;
    }
    setShipMissing([]);
    setShipAck(false);
    setConfirmShipOpen(true);
  };

  const handleEstadoChange = (nextEstado) => {
    if (
      nextEstado === "EN_CURSO" &&
      estadoUpper !== "EN_CURSO" &&
      !canTerminar
    ) {
      requestMarkShipped();
      return;
    }
    if (nextEstado === "ENTREGADO" && estadoUpper !== "ENTREGADO") {
      setDeliverAck(false);
      setDeliverDateDraft(
        String(envio.FechaEntregaDestino || "").slice(0, 10) ||
          new Date().toISOString().slice(0, 10),
      );
      setConfirmDeliveredOpen(true);
      return;
    }
    setShipMissing([]);
    setField("Estado", nextEstado);
  };

  /** Destino tipado del form → hints Matriz + Contacto (persona de envío). */
  const withDestinoHints = (base = {}) => {
    const tipo = String(destinoForm?.TIPO_DESTINO || "")
      .trim()
      .toUpperCase();
    const next = { ...base };
    if (["CLIENTE", "PROVEEDOR", "INTERNO"].includes(tipo)) {
      next.TIPO_DESTINO = tipo;
    }
    if (destinoForm?.ID_CLIENTE != null && destinoForm.ID_CLIENTE !== "") {
      next.ID_CLIENTE = destinoForm.ID_CLIENTE;
    }
    if (destinoForm?.CODPROV != null && destinoForm.CODPROV !== "") {
      next.CODPROV = destinoForm.CODPROV;
    }
    // Persona de envío vive en destinoForm; forzar Contacto al guardar
    const persona = String(destinoForm?.PERSONA_ENVIO || "").trim();
    if (persona) {
      next.Contacto = persona;
    } else if (
      destinoForm &&
      Object.prototype.hasOwnProperty.call(destinoForm, "PERSONA_ENVIO")
    ) {
      next.Contacto = next.Contacto || "";
    }
    return next;
  };

  const confirmMarkShipped = () => {
    if (!canMarkCourierShipped(envio) || !shipAck) return;
    setConfirmShipOpen(false);
    setShipAck(false);
    setShipMissing([]);
    const today = new Date().toISOString().slice(0, 10);
    if (typeof onSave === "function") {
      onSave(
        withDestinoHints({
          ...envio,
          Estado: "EN_CURSO",
          FechaEnvio: envio.FechaEnvio || today,
        }),
      );
    }
  };

  const confirmMarkDelivered = () => {
    if (!deliverAck) return;
    const today = new Date().toISOString().slice(0, 10);
    const fechaEntrega =
      String(deliverDateDraft || "").slice(0, 10) ||
      String(envio.FechaEntregaDestino || "").slice(0, 10) ||
      today;
    setConfirmDeliveredOpen(false);
    setDeliverAck(false);
    if (typeof onSave === "function") {
      onSave(
        withDestinoHints({
          ...envio,
          Estado: "ENTREGADO",
          FechaEntregaDestino: fechaEntrega,
        }),
      );
    } else {
      setField("Estado", "ENTREGADO");
      setField("FechaEntregaDestino", fechaEntrega);
    }
  };

  const applyOrigin = (origen) => {
    if (originLocked) return;
    const nextPais =
      inferCountryFromOrigin(origen, proveedores) || envio.Pais || "";
    onChange({ ...envio, Origen: origen, Pais: nextPais });
  };

  const applyDestinoForm = (nextDest) => {
    setDestinoForm(nextDest);
    const destinoLabel = destinoLabelFromEnvioDestino(nextDest, {
      customers,
      proveedores,
    });
    const persona = String(nextDest?.PERSONA_ENVIO || "").trim();
    const dirEnvio = String(nextDest?.DIRECCION_ENVIO || "").trim();
    onChange({
      ...envio,
      Destino: destinoLabel || envio.Destino || "",
      DireccionEnvio: dirEnvio,
      Contacto: persona,
    });
  };

  const setLine = (index, next) => {
    const lineas = envio.lineas.map((linea, i) => (i === index ? next : linea));
    onChange({ ...envio, lineas });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose?.();
        }}
        disableEscapeKeyDown
        slotProps={lockedDialogSlotProps}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.75),
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 18px 48px rgba(0,0,0,0.55)"
                : "0 20px 50px rgba(15,23,42,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 2,
            px: 2.5,
            borderBottom: "1px solid",
            borderColor: alpha(statusAccent(theme, envio.Estado), 0.35),
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(statusAccent(theme, envio.Estado), 0.32)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 58%)`
                : `linear-gradient(135deg, ${alpha(statusAccent(theme, envio.Estado), 0.18)} 0%, ${alpha(statusAccent(theme, envio.Estado), 0.06)} 48%, ${theme.palette.background.paper} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.25,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(statusAccent(theme, envio.Estado), 0.16),
                  color: statusAccent(theme, envio.Estado),
                }}
              >
                <LocalShippingOutlinedIcon />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.2 }}
                >
                  {envio.IdCourierControl
                    ? t("courierControl.editTitle")
                    : t("courierControl.newRequest")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.35, fontWeight: 500, maxWidth: 480 }}
                >
                  {t("courierControl.dialogSubtitle")}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {isLeaveClientEnvio ? (
                <Chip
                  size="small"
                  label={t("courierControl.leftWithClientBadge")}
                  sx={{
                    fontWeight: 800,
                    bgcolor: alpha("#6a1b9a", 0.12),
                    color: "#6a1b9a",
                    border: `1px solid ${alpha("#6a1b9a", 0.4)}`,
                  }}
                />
              ) : null}
              <Chip
                size="small"
                label={`${piezasCount} ${t("courierControl.pieces")}`}
                sx={{
                  fontWeight: 800,
                  ...(piezasKind.kind === "swatches"
                    ? {
                        bgcolor: alpha("#f9a825", 0.18),
                        color: "#f57f17",
                      }
                    : piezasKind.kind === "mixed"
                      ? {
                          bgcolor: alpha("#ef6c00", 0.16),
                          color: "#e65100",
                        }
                      : {
                          bgcolor: alpha(theme.palette.success.main, 0.14),
                          color: theme.palette.success.dark,
                        }),
                }}
              />
              <Chip
                size="small"
                label={
                  envio.Direccion === "ENTRADA"
                    ? t("courierControl.entry")
                    : t("courierControl.exit")
                }
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  envio.Ambito === "INTERNACIONAL"
                    ? t("courierControl.international")
                    : t("courierControl.national")
                }
                sx={{ fontWeight: 700 }}
              />
              {!isLeaveClientEnvio ? (
                <Chip
                  size="small"
                  color={statusColor(envio.Estado)}
                  label={
                    envio.Estado === "ENTREGADO"
                      ? t("courierControl.statusEntregado")
                      : envio.Estado === "EN_CURSO"
                        ? t("courierControl.statusEnCurso")
                        : envio.Estado === "PREPARANDO_ADUANAS"
                          ? t("courierControl.statusPreparandoAduanas")
                          : envio.Estado === "CANCELADO"
                            ? t("courierControl.statusCancelado")
                            : t("courierControl.statusPedido")
                  }
                  sx={{ fontWeight: 800 }}
                />
              ) : null}
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 1.5, sm: 2.25 },
            py: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.default, 0.45)
                : alpha(theme.palette.grey[50], 0.85),
            ...(requestDisabled
              ? {
                  "& .cc-request .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                  // Estado lo controla statusDisabled (Terminar puede volver a Pedido)
                  "& .cc-request .cc-status .MuiInputBase-root": {
                    pointerEvents: statusDisabled ? "none" : "auto",
                    opacity: statusDisabled ? 0.72 : 1,
                  },
                  // Piezas: se pueden desplegar para ver; los inputs siguen disabled
                }
              : null),
            ...(finishDisabled
              ? {
                  "& .cc-finish .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                  // Coste real editable con Terminar aunque el envío esté bloqueado
                  "& .cc-finish .cc-coste-real .MuiInputBase-root": {
                    pointerEvents: canTerminar ? "auto" : "none",
                    opacity: canTerminar ? 1 : 0.72,
                  },
                }
              : null),
            ...(statusDisabled
              ? {
                  "& .cc-status .MuiInputBase-root": {
                    pointerEvents: "none",
                    opacity: 0.72,
                  },
                }
              : null),
          }}
        >
          <FormPanel
            className="cc-request"
            icon={<RouteOutlinedIcon fontSize="small" />}
            title={t("courierControl.sectionRoute")}
            subtitle={t("courierControl.sectionRouteHint")}
            accent={theme.palette.primary.main}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.direction")}
                  value={envio.Direccion}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Direccion", e.target.value)}
                >
                  <MenuItem value="SALIDA">{t("courierControl.exit")}</MenuItem>
                  <MenuItem value="ENTRADA">
                    {t("courierControl.entry")}
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.scope")}
                  value={envio.Ambito}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Ambito", e.target.value)}
                >
                  <MenuItem value="NACIONAL">
                    {t("courierControl.national")}
                  </MenuItem>
                  <MenuItem value="INTERNACIONAL">
                    {t("courierControl.international")}
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("courierControl.dateOrder")}
                  InputLabelProps={{ shrink: true }}
                  value={String(envio.Fecha || "").slice(0, 10)}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("Fecha", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("courierControl.dateShip")}
                  InputLabelProps={{ shrink: true }}
                  value={String(envio.FechaEnvio || "").slice(0, 10)}
                  disabled={!hasEditPerm || (formLocked && !canTerminar)}
                  sx={fieldSx}
                  onChange={(e) => setField("FechaEnvio", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("courierControl.dateDelivery")}
                  InputLabelProps={{ shrink: true }}
                  value={String(envio.FechaEntregaDestino || "").slice(0, 10)}
                  disabled={
                    // Editable con Terminar mientras no esté cancelado (también en Enviado)
                    !canTerminar || estadoUpper === "CANCELADO"
                  }
                  sx={fieldSx}
                  onChange={(e) =>
                    setField("FechaEntregaDestino", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3} className="cc-status">
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.statusShipment")}
                  value={envio.Estado || "PEDIDO"}
                  disabled={statusDisabled}
                  sx={fieldSx}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                >
                  {canTerminar || estadoUpper === "PEDIDO" ? (
                    <MenuItem value="PEDIDO">
                      {t("courierControl.statusPedido")}
                    </MenuItem>
                  ) : null}
                  {showPreparandoAduanas ||
                  estadoUpper === "PREPARANDO_ADUANAS" ? (
                    <MenuItem
                      value="PREPARANDO_ADUANAS"
                      disabled={!canTerminar}
                    >
                      {t("courierControl.statusPreparandoAduanas")}
                    </MenuItem>
                  ) : null}
                  <MenuItem value="EN_CURSO">
                    {t("courierControl.statusEnCurso")}
                  </MenuItem>
                  {canTerminar || estadoUpper === "ENTREGADO" ? (
                    <MenuItem value="ENTREGADO" disabled={!canTerminar}>
                      {t("courierControl.statusEntregado")}
                    </MenuItem>
                  ) : null}
                  {canTerminar || estadoUpper === "CANCELADO" ? (
                    <MenuItem value="CANCELADO" disabled={!canTerminar}>
                      {t("courierControl.statusCancelado")}
                    </MenuItem>
                  ) : null}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                {originLocked ? (
                  <TextField
                    fullWidth
                    size="small"
                    label={t("courierControl.origin")}
                    value={STAGE_CHINA_ORIGIN}
                    disabled
                    sx={fieldSx}
                  />
                ) : (
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={originOptions}
                    value={envio.Origen || ""}
                    disabled={requestDisabled}
                    filterOptions={(opts, state) =>
                      filterOptionsStartsWith(opts, state, (o) =>
                        typeof o === "string" ? o : String(o || ""),
                      )
                    }
                    onChange={(_, value) =>
                      applyOrigin(
                        typeof value === "string" ? value : value || "",
                      )
                    }
                    onInputChange={(_, value, reason) => {
                      if (reason === "input" || reason === "clear") {
                        applyOrigin(value || "");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("courierControl.origin")}
                        sx={fieldSx}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.country")}
                  value={envio.Pais || ""}
                  disabled={requestDisabled || originLocked}
                  sx={fieldSx}
                  onChange={(e) => setField("Pais", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {isChinaUser ? (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("courierControl.requestedBy")}
                    value={envio.SolicitadoPor || ""}
                    disabled={requestDisabled}
                    sx={fieldSx}
                    onChange={(e) => setField("SolicitadoPor", e.target.value)}
                  >
                    {envio.SolicitadoPor &&
                    !requestedByOptions.some(
                      (u) => userDisplayName(u) === envio.SolicitadoPor,
                    ) ? (
                      <MenuItem value={envio.SolicitadoPor}>
                        {envio.SolicitadoPor}
                      </MenuItem>
                    ) : null}
                    {requestedByOptions.map((u) => {
                      const label = userDisplayName(u);
                      return (
                        <MenuItem key={u.id || label} value={label}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                ) : (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("courierControl.requestedBy")}
                    value={envio.SolicitadoPor || ""}
                    disabled={requestDisabled}
                    sx={fieldSx}
                    onChange={(e) => setField("SolicitadoPor", e.target.value)}
                  >
                    {envio.SolicitadoPor &&
                    !userNameOptions.includes(envio.SolicitadoPor) ? (
                      <MenuItem value={envio.SolicitadoPor}>
                        {envio.SolicitadoPor}
                      </MenuItem>
                    ) : null}
                    {userNameOptions.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: "1px dashed",
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                    bgcolor: alpha(theme.palette.primary.main, 0.035),
                  }}
                >
                  <EnvioDestinoFields
                    value={destinoForm}
                    onChange={applyDestinoForm}
                    disabled={requestDisabled}
                    compact
                    fieldSx={fieldSx}
                    allowedTipos={allowedDestinoTipos}
                    internoLabel={t("courierControl.destinationSdsSpain")}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("courierControl.courier")}
                  value={envio.CompaniaCourier || ""}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("CompaniaCourier", e.target.value)}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {courierCompanyOptions.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("courierControl.courierNumber")}
                  value={envio.NumeroAwb || ""}
                  disabled={requestDisabled}
                  sx={fieldSx}
                  onChange={(e) => setField("NumeroAwb", e.target.value)}
                />
              </Grid>
            </Grid>
            {shipMissing.length ? (
              <Alert
                severity="warning"
                sx={{ mt: 1.5, borderRadius: 2 }}
                onClose={() => setShipMissing([])}
              >
                {t("courierControl.confirmShipMissing", {
                  fields: shipMissing
                    .map((key) => t(`courierControl.${key}`))
                    .join(", "),
                })}
              </Alert>
            ) : null}
            {canSolicitar && !canTerminar && !shippedLocked ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 1.5,
                  px: 1.25,
                  py: 0.85,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.2),
                }}
              >
                {t("courierControl.solicitarCanEditHint")}
              </Typography>
            ) : null}
          </FormPanel>

          <FormPanel
            className="cc-finish"
            icon={<Inventory2OutlinedIcon fontSize="small" />}
            title={t("courierControl.sectionBox")}
            subtitle={
              packingIsHand || isLeaveClientEnvio
                ? t("courierControl.packingByHand")
                : t("courierControl.sectionBoxHint")
            }
            accent={theme.palette.success.main}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              alignItems={{ md: "center" }}
              sx={{ mb: packingIsHand || isLeaveClientEnvio ? 0 : 1.5 }}
            >
              <TextField
                select
                size="small"
                label={t("courierControl.packing")}
                value={envio.TipoEmbalaje || ""}
                disabled={finishDisabled}
                sx={{ ...fieldSx, flex: 1.5, minWidth: 0 }}
                onChange={(e) => setField("TipoEmbalaje", e.target.value)}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {envio.TipoEmbalaje &&
                !COURIER_PACKING_OPTIONS.includes(envio.TipoEmbalaje) ? (
                  <MenuItem value={envio.TipoEmbalaje}>
                    {envio.TipoEmbalaje}
                  </MenuItem>
                ) : null}
                {[...COURIER_PACKING_OPTIONS]
                  .sort((a, b) => {
                    const la = courierPackingI18nKey(a)
                      ? t(courierPackingI18nKey(a))
                      : a;
                    const lb = courierPackingI18nKey(b)
                      ? t(courierPackingI18nKey(b))
                      : b;
                    return String(la).localeCompare(String(lb), undefined, {
                      sensitivity: "base",
                    });
                  })
                  .map((key) => {
                    const i18nKey = courierPackingI18nKey(key);
                    return (
                      <MenuItem key={key} value={key}>
                        {i18nKey ? t(i18nKey) : key}
                      </MenuItem>
                    );
                  })}
              </TextField>
              <Button
                size="small"
                onClick={() => setBoxDetailsOpen((v) => !v)}
                endIcon={
                  boxDetailsOpen ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                  alignSelf: { xs: "flex-start", md: "center" },
                }}
              >
                {t("courierControl.boxDetailsToggle", {
                  defaultValue: boxDetailsOpen
                    ? "Ocultar medidas"
                    : "Medidas y costes",
                })}
              </Button>
            </Stack>
            <Collapse in={boxDetailsOpen}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.25}
                sx={{ mb: 1.5, mt: 1 }}
              >
                <TextField
                  size="small"
                  label={t("courierControl.length")}
                  value={envio.LargoCm ?? ""}
                  disabled={finishDisabled}
                  sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                  onChange={(e) => setField("LargoCm", e.target.value)}
                />
                <TextField
                  size="small"
                  label={t("courierControl.width")}
                  value={envio.AnchoCm ?? ""}
                  disabled={finishDisabled}
                  sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                  onChange={(e) => setField("AnchoCm", e.target.value)}
                />
                <TextField
                  size="small"
                  label={t("courierControl.height")}
                  value={envio.AltoCm ?? ""}
                  disabled={finishDisabled}
                  sx={{ ...fieldSx, flex: 0.7, minWidth: 72 }}
                  onChange={(e) => setField("AltoCm", e.target.value)}
                />
                <TextField
                  size="small"
                  label={t("courierControl.grossWeight")}
                  value={envio.PesoBrutoKg ?? ""}
                  disabled={finishDisabled}
                  sx={{ ...fieldSx, flex: 0.85, minWidth: 80 }}
                  onChange={(e) => setField("PesoBrutoKg", e.target.value)}
                />
                <TextField
                  size="small"
                  label={t("courierControl.cost")}
                  value={envio.Coste ?? ""}
                  disabled={finishDisabled}
                  sx={{ ...fieldSx, flex: 0.8, minWidth: 72 }}
                  onChange={(e) => setField("Coste", e.target.value)}
                />
                <TextField
                  size="small"
                  className="cc-coste-real"
                  label={t("courierControl.realCost")}
                  value={envio.CosteReal ?? ""}
                  disabled={costeRealDisabled}
                  sx={{ ...fieldSx, flex: 0.9, minWidth: 80 }}
                  onChange={(e) => setField("CosteReal", e.target.value)}
                />
              </Stack>
              <Stack
                direction="row"
                flexWrap="wrap"
                useFlexGap
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.success.main, 0.25),
                  background: `linear-gradient(90deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.06)})`,
                }}
              >
                <Chip
                  size="small"
                  label={`${t("courierControl.volume")}: ${fmt(measures.volumenM3, 6)} m³`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${t("courierControl.volWeight")}: ${fmt(measures.pesoVolumetricoKg)} kg`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${t("courierControl.chargeable")}: ${fmt(measures.pesoFacturableKg)} kg`}
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  color={
                    measures.diferencia != null && measures.diferencia !== 0
                      ? "warning"
                      : "default"
                  }
                  label={`${t("courierControl.difference")}: ${
                    measures.diferencia == null
                      ? "—"
                      : `${fmt(measures.diferencia, 2)} €`
                  }`}
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Collapse>
          </FormPanel>

          <FormPanel
            className="cc-pieces"
            icon={<WidgetsOutlinedIcon fontSize="small" />}
            title={`${t("courierControl.sectionPieces")} · ${piezasCount}`}
            subtitle={
              isLeaveClientEnvio
                ? t("courierControl.leftWithClientBadge")
                : t("courierControl.piecesHintShort", {
                    defaultValue: t("courierControl.multiLineHint"),
                  })
            }
            accent={
              isLeaveClientEnvio
                ? "#6a1b9a"
                : theme.palette.secondary?.main || theme.palette.info.main
            }
          >
            <Stack spacing={1}>
              {(envio.lineas || []).map((linea, index) => {
                const pieceKey = pieceKeyOf(linea, index);
                const prevLine =
                  index > 0 ? (envio.lineas || [])[index - 1] : null;
                return (
                  <PieceCard
                    key={pieceKey}
                    linea={linea}
                    index={index}
                    previousLine={prevLine}
                    fieldSx={fieldSx}
                    t={t}
                    expanded={openPieceKey === pieceKey}
                    onToggleExpand={() =>
                      setOpenPieceKey((cur) =>
                        cur === pieceKey ? null : pieceKey,
                      )
                    }
                    preferLeftWithClient={isCourierLeftWithClientMotivo(
                      envio?.Notas,
                    )}
                    requestDisabled={requestDisabled}
                    pieceFieldsDisabled={piecesLocked}
                    canRemove={
                      !requestDisabled &&
                      !piecesLocked &&
                      (envio.lineas || []).length > 1
                    }
                    onChange={(next) => setLine(index, next)}
                    onRemove={() => {
                      const estilo = String(linea.Estilo || "").trim();
                      const codigo = String(linea.CodigoMuestra || "").trim();
                      const label =
                        estilo || codigo || t("courierControl.piece");
                      const motivo = normalizeCourierMotivoValue(linea.Motivo);
                      setPendingRemove({
                        index,
                        label,
                        estilo,
                        codigo,
                        motivo,
                        linked: Boolean(
                          linea.IdMovimientoMuestra || linea.IdMuestra,
                        ),
                        hasMovimiento: Boolean(linea.IdMovimientoMuestra),
                      });
                    }}
                  />
                );
              })}
            </Stack>

            {shippedLocked ? (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                {canTerminar
                  ? t("courierControl.statusShippedLockedHintTerminar")
                  : t("courierControl.statusShippedLockedHint")}
              </Alert>
            ) : null}
          </FormPanel>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            justifyContent: "space-between",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {canDownloadDocs ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  disabled={Boolean(docsBusy) || saving}
                  onClick={() => handleDownloadDoc("informe")}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  {docsBusy === "informe"
                    ? t("courierControl.docsGenerating")
                    : t("courierControl.docsInforme")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DescriptionOutlinedIcon />}
                  disabled={Boolean(docsBusy) || saving}
                  onClick={(e) => setFacturaMenuAnchor(e.currentTarget)}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  {docsBusy === "factura"
                    ? t("courierControl.docsGenerating")
                    : t("courierControl.docsFactura")}
                </Button>
                <Menu
                  anchorEl={facturaMenuAnchor}
                  open={Boolean(facturaMenuAnchor)}
                  onClose={() => setFacturaMenuAnchor(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  <MenuItem disabled sx={{ opacity: 1, fontWeight: 700 }}>
                    {t("courierControl.docsShipFromTitle")}
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleDownloadDoc("factura", { shipFrom: "STAGE" })
                    }
                  >
                    {t("courierControl.docsShipFromStage")}
                    {suggestedShipFrom === "STAGE"
                      ? ` · ${t("courierControl.docsShipFromSuggested")}`
                      : ""}
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleDownloadDoc("factura", { shipFrom: "NML" })
                    }
                  >
                    {t("courierControl.docsShipFromNml")}
                    {suggestedShipFrom === "NML"
                      ? ` · ${t("courierControl.docsShipFromSuggested")}`
                      : ""}
                  </MenuItem>
                </Menu>
              </>
            ) : null}
            {canDelete ? (
              <Button
                color="error"
                onClick={onDelete}
                disabled={saving}
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
              >
                {t("courierControl.delete")}
              </Button>
            ) : null}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <FormControlLabel
              sx={{
                mr: 0.5,
                ml: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.78rem",
                  fontWeight: 600,
                },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={repeatSeasonBrand}
                  onChange={(e) => setRepeatSeasonBrand(e.target.checked)}
                  disabled={addPieceDisabled}
                />
              }
              label={t("courierControl.repeatSeasonBrand")}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={addPieceDisabled}
              title={
                addPieceDisabled && !requestDisabled
                  ? t("courierControl.addLineLockedShipped")
                  : undefined
              }
              onClick={addPiece}
              sx={{ fontWeight: 700, textTransform: "none", borderRadius: 2 }}
            >
              {t("courierControl.addLine")}
            </Button>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("courierControl.cancel")}
            </Button>
            {canSave ? (
              <Button
                variant="contained"
                onClick={() => onSave(withDestinoHints(envio))}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  px: 2.5,
                }}
              >
                {t("courierControl.save")}
              </Button>
            ) : null}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingRemove)}
        onClose={(_e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setPendingRemove(null);
        }}
        disableEscapeKeyDown
        slotProps={lockedDialogSlotProps}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.error.main, 0.12),
                color: "error.main",
                flexShrink: 0,
              }}
            >
              <OutboxOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.25 }}
              >
                {t("courierControl.removePieceTitle")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("courierControl.removePieceLead")}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                border: "1px solid",
                borderColor: alpha(theme.palette.error.main, 0.28),
                bgcolor: alpha(theme.palette.error.main, 0.04),
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 800 }}>
                {pendingRemove?.label || "—"}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.75 }}
              >
                {pendingRemove?.codigo &&
                pendingRemove.codigo !== pendingRemove.label ? (
                  <Chip
                    size="small"
                    label={pendingRemove.codigo}
                    sx={{ height: 22, fontWeight: 700 }}
                  />
                ) : null}
                {pendingRemove?.motivo ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(
                      courierMotivoI18nKey(pendingRemove.motivo) ||
                        `courierControl.reason${String(pendingRemove.motivo)
                          .charAt(0)
                          .toUpperCase()}${String(pendingRemove.motivo).slice(1)}`,
                    )}
                    sx={{ height: 22, fontWeight: 600 }}
                  />
                ) : null}
                {pendingRemove?.hasMovimiento ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={t("courierControl.fromMatrixShipment")}
                    sx={{ height: 22, fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Box>

            {pendingRemove?.linked ? (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                {t("courierControl.removePieceLinkedHint")}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("courierControl.removePieceConfirmSimple")}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
              {t("courierControl.removePieceSaveHint")}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setPendingRemove(null)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              if (!pendingRemove) return;
              onChange({
                ...envio,
                lineas: envio.lineas.filter(
                  (_, i) => i !== pendingRemove.index,
                ),
              });
              setPendingRemove(null);
            }}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("courierControl.removePieceAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmShipOpen}
        onClose={(_e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setConfirmShipOpen(false);
          setShipAck(false);
        }}
        disableEscapeKeyDown
        slotProps={lockedDialogSlotProps}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("courierControl.confirmShipTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("courierControl.confirmShipLead")}
            </Typography>
            <Alert
              severity="warning"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              {t("courierControl.confirmShipIrreversible")}
            </Alert>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: shipAck
                  ? alpha(theme.palette.success.main, 0.45)
                  : "divider",
                bgcolor: shipAck
                  ? alpha(theme.palette.success.main, 0.06)
                  : alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", gap: 0.5 }}
                control={
                  <Checkbox
                    checked={shipAck}
                    onChange={(e) => setShipAck(e.target.checked)}
                    color="success"
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, pt: 0.35 }}
                  >
                    {t("courierControl.confirmShipAck")}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmShipOpen(false);
              setShipAck(false);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!shipAck}
            onClick={confirmMarkShipped}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("courierControl.confirmShipAction")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeliveredOpen}
        onClose={(_e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setConfirmDeliveredOpen(false);
          setDeliverAck(false);
          setDeliverDateDraft("");
        }}
        disableEscapeKeyDown
        slotProps={lockedDialogSlotProps}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("courierControl.confirmDeliveredTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("courierControl.confirmDeliveredLead", {
                count: deliveredSampleLines.length || 0,
              })}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t("courierControl.dateDelivery")}
              InputLabelProps={{ shrink: true }}
              value={deliverDateDraft}
              onChange={(e) => setDeliverDateDraft(e.target.value)}
              sx={fieldSx}
              helperText={t("courierControl.dateDeliveryHint", {
                defaultValue: "Fecha en la que el envío llega al destino.",
              })}
            />
            {deliveredSampleLines.length > 0 ? (
              <Box
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  maxHeight: 220,
                  overflow: "auto",
                  px: 1.5,
                  py: 0.75,
                }}
              >
                {deliveredSampleLines.map((linea, idx) => (
                  <Box
                    key={
                      linea.IdCourierControlLinea ||
                      `${linea.IdMuestra || "x"}-${idx}`
                    }
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.55,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {linea.Estilo ||
                        linea.CodigoMuestra ||
                        (linea.IdMuestra ? `#${linea.IdMuestra}` : "—")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        linea.Estilo && linea.CodigoMuestra
                          ? linea.CodigoMuestra
                          : null,
                        linea.Motivo || "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("courierControl.confirmDeliveredNoSamples")}
              </Typography>
            )}
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              {t("courierControl.confirmDeliveredSamplesHint")}
            </Alert>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: deliverAck
                  ? alpha(theme.palette.success.main, 0.45)
                  : "divider",
                bgcolor: deliverAck
                  ? alpha(theme.palette.success.main, 0.06)
                  : alpha(theme.palette.action.hover, 0.35),
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", gap: 0.5 }}
                control={
                  <Checkbox
                    checked={deliverAck}
                    onChange={(e) => setDeliverAck(e.target.checked)}
                    color="success"
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, pt: 0.35 }}
                  >
                    {t("courierControl.confirmDeliveredAck")}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmDeliveredOpen(false);
              setDeliverAck(false);
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!deliverAck}
            onClick={confirmMarkDelivered}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("courierControl.confirmDeliveredAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
